<template>
  <div class="fts-formula-display">
    <fts-tippy v-if="tooltip">
      <div
        slot="trigger"
        v-html="content"
        class="formula-display formula-display--toggle"
        :class="{ 'formula-display--nowrap': noWrap }"
      ></div>
      <div slot="content" v-html="content" class="formula-display"></div>
    </fts-tippy>

    <div
      v-html="content"
      class="formula-display"
      :class="{ 'formula-display--nowrap': noWrap }"
      v-else
    ></div>
  </div>
</template>

<script>
import commons from "../commons/commons";
import { prepareVariables, filterInformation } from "./formula/formula";
import FTSTippy from "../elements/FTSTippy";

export default {
  props: {
    noWrap: Boolean,
    tooltip: Boolean,
    /* Optional: a host with no variable scope (the listing / dashboard filter designers) omits
       it. Default to an empty level so updateDisplay still resolves the constant chips instead
       of dying on `undefined.concat` and leaving the raw {{TODAY}} text on screen (SS05416). */
    variables: {
      type: Array,
      default: () => []
    },
    formula: String,
    form: Object
  },
  components: {
    "fts-tippy": FTSTippy
  },
  data: () => ({
    content: ""
  }),
  methods: {
    getModule(id) {
      if (id && this.$store.getters.workspaceModules) {
        const findModule = this.$store.getters.workspaceModules.find(
          x => x.id == id
        );
        if (findModule)
          return {
            id: findModule.id,
            name: findModule.name,
            emoji: findModule.emoji
          };
      }
    },
    async updateDisplay(formula) {
      let re = /{{(?<fieldName>[\w|\.]*?)}}/g;

      try {
        let match = re.exec(formula);
        this.content = formula;
        let selection = _.cloneDeep(this.variables);
        // include the computed-field constants too, so a saved {{ROWAUTONUMBER}} (or TODAY/NOW)
        // renders as its chip instead of raw template text — same merge the editor does on reopen
        selection = selection.concat(commons.formulaConstants, commons.computedFormulaConstants);

        while (match != null) {
          let variablePaths = match[1].split(".");
          let variableValues = [];
          let source = selection;
          let lastVariable,
            previousVariable = null;
          for (let variablePath of variablePaths) {
            lastVariable = source.find(x => x.fieldName == variablePath);

            if (
              !lastVariable &&
              previousVariable &&
              previousVariable.type == 14
            ) {
              source = await prepareVariables({
                information: previousVariable.fields,
                variableType: previousVariable.type,
                modules: this.$store.getters.workspaceModules,
                moduleId: previousVariable.moduleId,
                activityId: previousVariable.activityId
              });
              lastVariable = source.find(x => x.fieldName == variablePath);
            }

            variableValues.push(lastVariable);

            // {{query.code}} / {{event.items}} (automation-blocks-simplification P9): the rest are free keys
            if (commons.isLenientPayloadChip(variableValues)) {
              variablePaths.slice(1).forEach(key => variableValues.push({ name: key, fieldName: key }));
              break;
            }

            // a constant with fields of its own ({{CURRENTUSER.DisplayName}}): the next segment is one of them
            if (lastVariable && lastVariable.constantFields) {
              source = lastVariable.constantFields;
              continue;
            }

            /* a form-scoped variable ("Form") expands from its form design, not from the
               module's information — Control/Input elements only exist on the design, so
               resolving them through module information loses them and the chip falls back
               to raw {{form.xxx}} text. Same source FTSFormula walks. */
            if (
              lastVariable &&
              (lastVariable.form || (lastVariable.name == "Form" && this.form))
            ) {
              source = await prepareVariables({
                information: filterInformation(
                  commons.getAllElements(null, lastVariable.form || this.form)
                ),
                variableType: lastVariable.type,
                modules: this.$store.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            } else if (lastVariable && lastVariable.moduleId) {
              let data = await this.$store.dispatch("findVariable", {
                moduleId: lastVariable.moduleId
              });

              if (lastVariable.informationId) {
                source = await prepareVariables({
                  information: data.information.find(
                    x => x.id == lastVariable.informationId
                  )["fields"],
                  variableType: lastVariable.type,
                  modules: this.$store.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              } else {
                source = await prepareVariables({
                  information: data.information,
                  variableType: lastVariable.type,
                  modules: this.$store.getters.workspaceModules,
                  moduleId: lastVariable.moduleId,
                  activityId: lastVariable.activityId
                });
              }
            } else if (lastVariable && lastVariable.type == 14) {
              source = await prepareVariables({
                information: lastVariable.fields,
                variableType: lastVariable.type,
                modules: this.$store.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            } else if (lastVariable && lastVariable.referenceModuleTableId) {
              let referenceModule = await this.$store.dispatch("findVariable", {
                moduleId: lastVariable.referenceModuleId
              });

              let referenceTable = referenceModule.information.find(
                x => x.id == lastVariable.referenceModuleTableId
              );

              source = await prepareVariables({
                information: referenceTable.fields,
                variableType: lastVariable.variableType
                  ? lastVariable.variableType
                  : lastVariable.type
                ,
                modules: this.$store.getters.workspaceModules,
                moduleId: lastVariable.moduleId,
                activityId: lastVariable.activityId
              });
            }
          }

          this.content = this.content.replace(
            match[0],
            `<fts-i-unit contenteditable="false" field="${
              match[1]
            }">${commons.informationIconHtml(
              variableValues[variableValues.length - 1],
              this.$store.getters.workspaceModules
            )}&nbsp;${variableValues
              .map(x => `<span class="variable">${x.name}</span>`)
              .join(
                "<span class='path-indicator'>'s</span>&nbsp;"
              )}</fts-i-unit>`
          );
          match = re.exec(formula);
        }
      } catch (err) {
        console.warn(`FTSExpression error: ${err}`);
      }
    }
  },
  watch: {
    async formula() {
      await this.updateDisplay(this.formula);
    }
  },
  async mounted() {
    await this.updateDisplay(this.formula);
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.formula-display {
  $py: 0.1rem;
  padding: $py 0;
  word-break: break-word;
  &.formula-display--toggle {
    cursor: default;
  }
  &.formula-display--nowrap {
    @include text-overflow-ellipsis;
  }
  .information-icon {
    vertical-align: middle;
    color: var(--fts-gray-400);
  }
  .variable {
    color: var(--fts-formula-ref, #b26a00);
    font-weight: 600;
  }
  .path-indicator {
    color: var(--fts-gray-400);
  }
}
</style>